import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NodeData } from '@/types/workflow';
import { User, MessageSquare, List, Copy, Trash2 } from 'lucide-react';

interface CRMActionNodeProps extends NodeProps {
  data: NodeData;
}

const getNodeConfig = (type: string) => {
  switch (type) {
    case 'lead_update':
      return {
        icon: User,
        title: 'Update Lead',
        description: 'Modify lead information',
        color: '#3b82f6',
        bgColor: 'bg-blue-500/20',
        borderColor: 'border-blue-500/30',
        ringColor: 'ring-blue-500/50',
        shadowColor: 'shadow-blue-500/20'
      };
    case 'email_sequence':
      return {
        icon: MessageSquare,
        title: 'Email Sequence',
        description: 'Add to email campaigns',
        color: '#10b981',
        bgColor: 'bg-green-500/20',
        borderColor: 'border-green-500/30',
        ringColor: 'ring-green-500/50',
        shadowColor: 'shadow-green-500/20'
      };
    case 'lead_list':
      return {
        icon: List,
        title: 'Create Lead List',
        description: 'Organize lead lists',
        color: '#6366f1',
        bgColor: 'bg-indigo-500/20',
        borderColor: 'border-indigo-500/30',
        ringColor: 'ring-indigo-500/50',
        shadowColor: 'shadow-indigo-500/20'
      };
    default:
      return {
        icon: User,
        title: 'CRM Action',
        description: 'CRM operation',
        color: '#6b7280',
        bgColor: 'bg-gray-500/20',
        borderColor: 'border-gray-500/30',
        ringColor: 'ring-gray-500/50',
        shadowColor: 'shadow-gray-500/20'
      };
  }
};

export const CRMActionNode = memo(({ data, selected, id, type }: CRMActionNodeProps) => {
  const [showToolbar, setShowToolbar] = useState(false);
  const { getNodes, setNodes, getEdges, setEdges } = useReactFlow();
  
  const config = getNodeConfig(type || 'lead_update');
  const Icon = config.icon;

  const updateNodeData = useCallback((updates: Partial<NodeData>) => {
    const nodes = getNodes();
    const updatedNodes = nodes.map(node => {
      if (node.id === id) {
        return {
          ...node,
          data: {
            ...node.data,
            ...updates
          }
        };
      }
      return node;
    });
    setNodes(updatedNodes);
  }, [id, getNodes, setNodes]);

  const handleDuplicate = useCallback(() => {
    const nodes = getNodes();
    const currentNode = nodes.find(node => node.id === id);
    if (currentNode) {
      const newNode = {
        ...currentNode,
        id: `${currentNode.type}_${Date.now()}`,
        position: {
          x: currentNode.position.x + 50,
          y: currentNode.position.y + 50,
        },
      };
      setNodes([...nodes, newNode]);
    }
  }, [id, getNodes, setNodes]);

  const handleDelete = useCallback(() => {
    const nodes = getNodes();
    const edges = getEdges();
    
    const updatedNodes = nodes.filter(node => node.id !== id);
    const updatedEdges = edges.filter(edge => edge.source !== id && edge.target !== id);
    
    setNodes(updatedNodes);
    setEdges(updatedEdges);
  }, [id, getNodes, getEdges, setNodes, setEdges]);

  const renderFields = () => {
    switch (type) {
      case 'lead_update':
        return (
          <>
            <div>
              <Label className="text-xs">Fields to Update</Label>
              <Textarea
                placeholder="Specify which lead fields to update (e.g., status, score, notes)..."
                value={data.updateFields || ''}
                onChange={(e) => updateNodeData({ updateFields: e.target.value })}
                className="min-h-[60px] resize-none text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">New Status</Label>
              <Select 
                value={data.newStatus || ''}
                onValueChange={(value) => updateNodeData({ newStatus: value })}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="unqualified">Unqualified</SelectItem>
                  <SelectItem value="opportunity">Opportunity</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'email_sequence':
        return (
          <>
            <div>
              <Label className="text-xs">Sequence Name</Label>
              <Input
                placeholder="e.g., Welcome Series, Follow-up Campaign"
                value={data.sequenceName || ''}
                onChange={(e) => updateNodeData({ sequenceName: e.target.value })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">Sequence Type</Label>
              <Select 
                value={data.sequenceType || ''}
                onValueChange={(value) => updateNodeData({ sequenceType: value })}
              >
                <SelectTrigger className="h-8">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="welcome">Welcome Series</SelectItem>
                  <SelectItem value="nurture">Nurture Campaign</SelectItem>
                  <SelectItem value="followup">Follow-up Sequence</SelectItem>
                  <SelectItem value="reengagement">Re-engagement</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        );
      case 'lead_list':
        return (
          <>
            <div>
              <Label className="text-xs">List Name</Label>
              <Input
                placeholder="e.g., High Value Prospects, Q4 Targets"
                value={data.listName || ''}
                onChange={(e) => updateNodeData({ listName: e.target.value })}
                className="h-8"
              />
            </div>
            <div>
              <Label className="text-xs">List Description</Label>
              <Textarea
                placeholder="Describe the purpose of this lead list..."
                value={data.listDescription || ''}
                onChange={(e) => updateNodeData({ listDescription: e.target.value })}
                className="min-h-[60px] resize-none text-xs"
              />
            </div>
          </>
        );
      default:
        return (
          <div>
            <Label className="text-xs">Configuration</Label>
            <Textarea
              placeholder="Configure the CRM action..."
              value={data.configuration || ''}
              onChange={(e) => updateNodeData({ configuration: e.target.value })}
              className="min-h-[60px] resize-none text-xs"
            />
          </div>
        );
    }
  };

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setShowToolbar(true)}
      onMouseLeave={() => setShowToolbar(false)}
    >
      {/* Toolbar */}
      {(showToolbar || selected) && (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
          <div className="flex items-center gap-1 bg-background/95 backdrop-blur-sm border rounded-lg shadow-lg px-2 py-1">
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-muted"
              onClick={handleDuplicate}
            >
              <Copy className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
              onClick={handleDelete}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      <Card 
        className={`min-w-[280px] backdrop-blur-md bg-background/75 ${config.borderColor} transition-all duration-200 ${
          selected 
            ? `ring-2 ${config.ringColor} shadow-lg ${config.shadowColor}` 
            : 'shadow-md hover:shadow-lg'
        }`}
      >
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg ${config.bgColor} flex items-center justify-center`}>
              <Icon className="w-4 h-4" style={{ color: config.color }} />
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">{data.label || config.title}</div>
              <div className="text-xs text-muted-foreground">{config.description}</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          {renderFields()}
        </CardContent>
        
        {/* Input Handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="input"
          className="custom-handle"
        />
        
        {/* Output Handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="custom-handle"
          style={{
            backgroundColor: config.color,
            boxShadow: `0 2px 8px ${config.color}50`,
          }}
        />
      </Card>
    </div>
  );
});

CRMActionNode.displayName = 'CRMActionNode';